import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AppVulnerability, AppVulnerabilitySummary } from "@/types/appVulnerability";
import { useToast } from "@/hooks/use-toast";

export function useAppVulnerabilities() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  console.log('[useAppVulnerabilities] Hook initialized');

  // Fetch all app vulnerabilities and group by CVE ID
  const { data: vulnerabilities = [], isLoading, error } = useQuery({
    queryKey: ["app-vulnerabilities"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("app_vulnerabilities")
        .select("*")
        .order("cvss_score", { ascending: false });
      
      if (error) throw error;
      
      // Fetch app icons from inventory
      const { data: inventoryData } = await supabase.rpc('get_app_inventory');
      const iconMap = new Map<string, string>();
      (inventoryData || []).forEach((app: any) => {
        if (app.icon) {
          iconMap.set(app.name, app.icon);
        }
      });
      
      // Group by CVE ID
      const grouped = new Map<string, AppVulnerability>();
      
      (data || []).forEach(record => {
        const cveId = record.cve_id;
        const appIcon = iconMap.get(record.app_name) || null;
        
        if (grouped.has(cveId)) {
          // Add to existing CVE's affected_apps
          const existing = grouped.get(cveId)!;
          existing.affected_apps.push({
            app_name: record.app_name,
            app_version: record.app_version,
            icon_url: appIcon,
          });
          // Sum up affected devices
          existing.affected_devices_count += record.device_ids?.length || 0;
        } else {
          // Create new CVE entry
          grouped.set(cveId, {
            id: record.id,
            cve_id: record.cve_id,
            severity: record.severity as 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | 'NONE',
            cvss_score: record.cvss_score,
            description: record.description || undefined,
            published_date: record.published_date,
            last_modified_date: record.last_modified_date,
            actively_exploited: false, // Will be populated from other sources
            affected_devices_count: record.device_ids?.length || 0,
            created_at: record.created_at,
            updated_at: record.updated_at,
            affected_apps: [{
              app_name: record.app_name,
              app_version: record.app_version,
              icon_url: appIcon,
            }],
            // Backwards compatibility
            app_name: record.app_name,
            app_version: record.app_version,
          });
        }
      });
      
      return Array.from(grouped.values());
    },
  });

  // Fetch vulnerability summary
  const { data: summary } = useQuery({
    queryKey: ["app-vulnerability-summary"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_app_vulnerability_summary");
      if (error) throw error;
      
      // Transform database response to match AppVulnerabilitySummary interface
      if (!data) return null;
      
      const dbData = data as any;
      return {
        total: dbData.total_vulnerabilities || 0,
        critical: dbData.critical_count || 0,
        high: dbData.high_count || 0,
        medium: dbData.medium_count || 0,
        low: dbData.low_count || 0,
        actively_exploited_count: dbData.actively_exploited_count || 0,
        actively_exploited_percentage: dbData.actively_exploited_percentage || 0,
        affected_apps_count: dbData.total_apps_affected || 0,
        affected_devices_count: dbData.total_devices_affected || 0,
        top_vulnerable_apps: dbData.top_vulnerable_apps || [],
      } as AppVulnerabilitySummary;
    },
  });

  // Trigger vulnerability scan
  const scanMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("scan-app-inventory-vulnerabilities", {
        body: { type: "manual", mode: "incremental", force_refresh: false }
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: "Scan completed",
        description: "Application vulnerability scan completed successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["app-vulnerabilities"] });
      queryClient.invalidateQueries({ queryKey: ["app-vulnerability-summary"] });
    },
    onError: (error) => {
      const errorDetails = [
        error.message,
        (error as any)?.cause,
        (error as any)?.statusText
      ].filter(Boolean).join(" — ");
      
      toast({
        title: "Scan failed",
        description: errorDetails || "Failed to scan application vulnerabilities.",
        variant: "destructive",
      });
    },
  });

  const hookReturn = {
    vulnerabilities,
    summary,
    isLoading,
    error,
    triggerScan: () => scanMutation.mutate(),
    isScanning: scanMutation.isPending,
  };

  console.log('[useAppVulnerabilities] Returning:', {
    vulnerabilitiesCount: vulnerabilities.length,
    isLoading,
    isScanning: scanMutation.isPending,
    hasTriggerScan: typeof hookReturn.triggerScan === 'function',
  });

  return hookReturn;
}
